import { memo, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

export interface SelectInputPropsValue {
  id: string;
  value: string | number;
  label: string;
}

interface Props extends React.HTMLProps<HTMLSelectElement> {
  values: SelectInputPropsValue[];
  label?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  hasDefault?: boolean;
  isDefaultValueSelectable?: boolean;
  defaultLabel?: string;
  containerClassName?: string;
  selectClassName?: string;
  isSecondary?: boolean;
  selectRef?: React.RefObject<HTMLSelectElement>;
  defaultOptionValue?: string;
  errorMessage?: string;
}

export const SelectInput = memo(
  ({
    values,
    label,
    onChange,
    hasDefault = false,
    defaultValue = '',
    defaultLabel,
    containerClassName,
    selectClassName,
    isSecondary = false,
    selectRef,
    style,
    defaultOptionValue = '',
    isDefaultValueSelectable = false,
    errorMessage,
    ...rest
  }: Props) => {
    const { t } = useTranslation();

    const [innerValue, setInnerValue] = useState(defaultValue);

    useEffect(() => {
      setInnerValue(defaultValue);
    }, [defaultValue]);

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      e.preventDefault();

      if (onChange) {
        onChange(e);
      }

      setInnerValue(e.target.value);
    };

    const restAttributes: Record<string, unknown> = { ...rest };

    return (
      <div>
        {label && (
          <label htmlFor={'rowsPerPage'} className="text-sm text-nowrap">
            {label}
          </label>
        )}
        <div className={clsx('flex w-full items-center relative gap-2', containerClassName)} style={style}>
          <div
            className={clsx('w-full', {
              'border-1 border-secondary-dark rounded-md': isSecondary,
              'border-red-500': errorMessage,
            })}>
            <select
              id="rowsPerPage"
              value={innerValue}
              {...restAttributes}
              ref={selectRef}
              onChange={handleChange}
              className={clsx(
                'w-auto py-3 px-5 text-center text-sm focus:outline-none border-transparent focus:ring-2 focus:bg-primary focus:border-transparent rounded-md border-r-8',
                {
                  'bg-transparent text-primary': isSecondary,
                  'bg-secondary-dark text-primary-dark': !isSecondary,
                  'cursor-not-allowed bg-gray-200': rest.disabled,
                  'cursor-pointer': !rest.disabled,
                },
                selectClassName
              )}>
              {hasDefault && (
                <option value={defaultOptionValue} disabled={!isDefaultValueSelectable}>
                  {defaultLabel ? t(defaultLabel) : t('common.select')}
                </option>
              )}
              {values.map((value) => (
                <option key={value.id} value={value.value}>
                  {value.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {errorMessage && <span className="text-xs text-red-500">{errorMessage}</span>}
      </div>
    );
  }
);
